import type { Ref } from 'react';
import { SearchIcon, CloseIcon } from '@utils/icons';
import styles from './SearchInput.module.css';

export interface ISearchInputProps {
  value: string;
  /** Fired with the next string value (not the raw event). Omit for a
   *  purely decorative rendering (pair with `disabled`). */
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Accessible label for the clear (×) button, shown only when non-empty
   *  and interactive. */
  ariaLabelClear?: string;
  className?: string;
  /** Renders the box inert — unfocusable input, no clear button. Used by
   *  the TopBar's decorative (not-yet-wired) search. */
  disabled?: boolean;
  /** Ref to the underlying `<input>`, e.g. so a keyboard shortcut can focus it. */
  inputRef?: Ref<HTMLInputElement>;
  /** Accessible name for the input when the placeholder is not enough. */
  ariaLabel?: string;
  /** Keyboard-shortcut hint (e.g. `/`) shown at the right while the box is empty. */
  shortcutHint?: string;
}

/**
 * Search box — leading magnifier, free-text input, and a clear (×) button
 * that appears only when there's a value. Extracted from the Genba / Trusstor
 * panes on the sync page, which rendered this markup verbatim. The `onChange`
 * passes the value through (callers want the string, not the event).
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  ariaLabelClear,
  className,
  disabled = false,
  inputRef,
  ariaLabel,
  shortcutHint,
}: ISearchInputProps) {
  return (
    <div className={[styles.searchBox, className].filter(Boolean).join(' ')}>
      <SearchIcon size={16} />
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        className={styles.searchInput}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {value && onChange && !disabled ? (
        <button
          type="button"
          className={styles.clear}
          onClick={() => onChange('')}
          aria-label={ariaLabelClear}
        >
          <CloseIcon size={13} />
        </button>
      ) : null}
      {shortcutHint && !value ? <kbd className={styles.hint}>{shortcutHint}</kbd> : null}
    </div>
  );
}
